import React, { useMemo, useState } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Icon from '@mui/material/Icon';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

function fmtDateTR(v) {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('tr-TR', { day: '2-digit', month: 'short' });
}

function fmtTimeTR(v) {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
}

function safeText(v, fallback = '—') {
  const s = (v ?? '').toString().trim();
  return s ? s : fallback;
}

function normalizeAttendanceStatus(v) {
  const raw = String(v ?? '').toUpperCase();
  if (['ATTENDED', 'PRESENT'].includes(raw)) return 'ATTENDED';
  if (['NO_SHOW', 'ABSENT'].includes(raw)) return 'NO_SHOW';
  if (raw === 'CANCELLED') return 'CANCELLED';
  if (['BURNED', 'BURN', 'FORFEITED'].includes(raw)) return 'BURNED';
  if (['PENDING', 'UNKNOWN', ''].includes(raw)) return 'PENDING';
  return raw;
}

function buildStatusChip(status) {
  if (status === 'ATTENDED') return { label: 'Katıldı', color: 'success' };
  if (status === 'NO_SHOW') return { label: 'Gelmedi', color: 'error' };
  if (status === 'CANCELLED') return { label: 'İptal', color: 'default' };
  if (status === 'BURNED') return { label: 'Yandı', color: 'warning' };
  return { label: 'Bekliyor', color: 'default' };
}

function normalizePaymentMethod(v) {
  const raw = String(v ?? '')
    .trim()
    .toUpperCase();
  if (['CARD', 'CREDIT_CARD', 'DEBIT_CARD', 'POS'].includes(raw)) return 'Kart';
  if (['CASH', 'NAKIT'].includes(raw)) return 'Nakit';
  if (['TRANSFER', 'BANK_TRANSFER', 'EFT', 'HAVALE', 'IBAN'].includes(raw)) return 'Havale';
  return 'Belirsiz';
}

function buildPaymentState(session) {
  const debt = Number(session?.planDebtTry || 0);
  const payStatus = String(session?.planPayStatus || '').toUpperCase();

  if (debt > 0 || ['UNPAID', 'PARTIAL', 'PENDING', 'OVERDUE'].includes(payStatus)) {
    return {
      label: debt > 0 ? `Borç: ${Math.round(debt).toLocaleString('tr-TR')} ₺` : 'Borçlu',
      color: 'warning',
    };
  }

  if (['PAID', 'SUCCESS'].includes(payStatus) || Number(session?.planPaidTry || 0) > 0) {
    return { label: 'Ödendi', color: 'success' };
  }

  return { label: 'Ödeme yok', color: 'default' };
}

function summarizeMember(sessions = []) {
  return sessions.reduce(
    (acc, item) => {
      const st = normalizeAttendanceStatus(item?.attendanceStatus);
      acc.total += 1;
      if (st === 'ATTENDED') acc.attended += 1;
      if (item?.isPayrollLeader && item?.includedInPayroll) {
        acc.includedSessions += 1;
        acc.earningTry += Number(item?.payrollAmountTry || 0);
      }
      if (!item?.isPayrollLeader) acc.redirectedSessions += 1;
      if (item?.pricingWarningText) acc.warningCount += 1;
      return acc;
    },
    {
      total: 0,
      attended: 0,
      includedSessions: 0,
      redirectedSessions: 0,
      earningTry: 0,
      warningCount: 0,
    }
  );
}

function buildGroupedView(rows = []) {
  const sessionMetaMap = new Map();
  const groupLeaderMap = new Map();

  for (const row of rows || []) {
    const key = String(row?.sessionId || '');
    if (!key) continue;

    if (!sessionMetaMap.has(key)) {
      sessionMetaMap.set(key, {
        sessionId: row.sessionId,
        participantRows: [],
        participantCount: 0,
      });
    }

    const meta = sessionMetaMap.get(key);
    meta.participantRows.push(row);
    meta.participantCount += 1;
  }

  for (const meta of sessionMetaMap.values()) {
    const participantRows = Array.isArray(meta.participantRows) ? meta.participantRows : [];
    const participantCount = Number(meta.participantCount || participantRows.length || 0);

    if (participantCount <= 1) {
      const single = participantRows[0] || {};
      meta.leaderParticipantId = single.participantId;
      meta.leaderMemberId = single.memberId ?? single.participantId ?? null;
      meta.leaderMemberName = single.memberName || '—';
      continue;
    }

    const groupKey = participantRows
      .map((item) => String(item?.memberId ?? item?.participantId ?? ''))
      .filter(Boolean)
      .sort()
      .join('|');

    if (!groupLeaderMap.has(groupKey)) {
      const sortedRows = participantRows
        .slice()
        .sort((a, b) =>
          String(a?.memberId ?? a?.participantId ?? '').localeCompare(
            String(b?.memberId ?? b?.participantId ?? ''),
            'tr'
          )
        );
      const leaderRow = sortedRows[0] || {};
      groupLeaderMap.set(groupKey, {
        leaderMemberId: leaderRow.memberId ?? leaderRow.participantId ?? null,
        leaderMemberName: leaderRow.memberName || '—',
      });
    }

    const groupLeader = groupLeaderMap.get(groupKey);
    const matchedLeaderRow =
      participantRows.find(
        (item) =>
          String(item?.memberId ?? item?.participantId ?? '') ===
          String(groupLeader?.leaderMemberId ?? '')
      ) ||
      participantRows[0] ||
      {};

    meta.leaderParticipantId = matchedLeaderRow.participantId;
    meta.leaderMemberId =
      groupLeader?.leaderMemberId ??
      matchedLeaderRow.memberId ??
      matchedLeaderRow.participantId ??
      null;
    meta.leaderMemberName = groupLeader?.leaderMemberName || matchedLeaderRow.memberName || '—';
  }

  const membersMap = new Map();

  for (const row of rows || []) {
    const memberKey = String(row?.memberId ?? row?.participantId ?? '');
    if (!memberKey) continue;

    if (!membersMap.has(memberKey)) {
      membersMap.set(memberKey, {
        memberKey,
        memberId: row?.memberId ?? null,
        memberName: row?.memberName || '—',
        packageNames: new Set(),
        sessions: [],
      });
    }

    const memberEntry = membersMap.get(memberKey);
    if (row?.packageName) memberEntry.packageNames.add(row.packageName);

    const sessionMeta = sessionMetaMap.get(String(row?.sessionId || ''));
    const isPayrollLeader = sessionMeta
      ? String(row?.participantId ?? '') === String(sessionMeta.leaderParticipantId ?? '')
      : true;

    memberEntry.sessions.push({
      ...row,
      participantCount: sessionMeta?.participantCount || Number(row?.sessionParticipantCount || 1),
      isPayrollLeader,
      payrollLeaderMemberName: sessionMeta?.leaderMemberName || row?.memberName || '—',
    });
  }

  return Array.from(membersMap.values())
    .map((member) => ({
      ...member,
      packageNames: Array.from(member.packageNames || []).sort((a, b) => a.localeCompare(b, 'tr')),
      sessions: member.sessions.sort((a, b) => new Date(b.startAt) - new Date(a.startAt)),
      summary: summarizeMember(member.sessions),
    }))
    .sort((a, b) => a.memberName.localeCompare(b.memberName, 'tr'));
}

function HeaderCell({ children, align = 'left' }) {
  return (
    <MDBox
      sx={{
        px: 1,
        py: 0.85,
        fontSize: 12,
        fontWeight: 700,
        color: 'text.secondary',
        textAlign: align,
      }}
    >
      {children}
    </MDBox>
  );
}

function BodyCell({ children, align = 'left' }) {
  return (
    <MDBox
      sx={{
        px: 1,
        py: 0.9,
        fontSize: 13,
        color: 'text.primary',
        textAlign: align,
        minWidth: 0,
      }}
    >
      {children}
    </MDBox>
  );
}

function SessionTable({ sessions, onToggleIncluded, onChangePayrollAmount }) {
  return (
    <MDBox
      sx={{
        border: '1px solid rgba(15,23,42,0.08)',
        borderRadius: '14px',
        overflow: 'hidden',
      }}
    >
      <MDBox
        sx={{
          display: 'grid',
          gridTemplateColumns: '120px 90px minmax(180px, 1fr) minmax(220px, 1.2fr) 100px 120px',
          bgcolor: 'rgba(248,250,252,0.95)',
          borderBottom: '1px solid rgba(15,23,42,0.08)',
        }}
      >
        <HeaderCell>Tarih / Saat</HeaderCell>
        <HeaderCell>Durum</HeaderCell>
        <HeaderCell>Ders / Paket</HeaderCell>
        <HeaderCell>Ödeme / Ücret</HeaderCell>
        <HeaderCell align="center">Dahil</HeaderCell>
        <HeaderCell align="right">Hakediş</HeaderCell>
      </MDBox>

      {sessions.map((session, index) => {
        const statusChip = buildStatusChip(normalizeAttendanceStatus(session.attendanceStatus));
        const paymentState = buildPaymentState(session);
        const paymentMethod = normalizePaymentMethod(
          session?.paymentMethod || session?.lastPaymentMethod || ''
        );

        return (
          <MDBox
            key={`${session.sessionId}-${session.participantId}`}
            sx={{
              display: 'grid',
              gridTemplateColumns: '120px 90px minmax(180px, 1fr) minmax(220px, 1.2fr) 100px 120px',
              borderBottom:
                index === sessions.length - 1 ? 'none' : '1px solid rgba(15,23,42,0.06)',
              bgcolor: session.isPayrollLeader ? '#fff' : 'rgba(248,250,252,0.55)',
              alignItems: 'center',
            }}
          >
            <BodyCell>
              <MDTypography variant="caption" fontWeight="medium">
                {fmtDateTR(session.startAt)}
              </MDTypography>
              <MDTypography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                {fmtTimeTR(session.startAt)}
              </MDTypography>
            </BodyCell>

            <BodyCell>
              <Chip
                size="small"
                color={statusChip.color}
                variant="outlined"
                label={statusChip.label}
              />
            </BodyCell>

            <BodyCell>
              <MDTypography variant="caption" fontWeight="medium">
                {safeText(session.activityName)}
              </MDTypography>
              <MDTypography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                {session.participantCount} kişilik seans
              </MDTypography>
              <MDTypography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                {safeText(session.packageName, 'Paket yok')}
              </MDTypography>
            </BodyCell>

            <BodyCell>
              <MDBox
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.45,
                  flexWrap: 'wrap',
                  mb: 0.35,
                }}
              >
                <Chip
                  size="small"
                  color={paymentState.color}
                  variant="outlined"
                  label={paymentState.label}
                />
                <Chip size="small" variant="outlined" label={paymentMethod} />
              </MDBox>

              {session.isPayrollLeader ? (
                <>
                  <MDTypography variant="caption" fontWeight="medium">
                    {safeText(session.appliedPricingLabel)}
                  </MDTypography>
                  <MDTypography
                    variant="caption"
                    sx={{ color: 'text.secondary', display: 'block' }}
                  >
                    {safeText(session.pricingStrategyLabel, 'Kişi sayısına göre ücret')}
                  </MDTypography>
                </>
              ) : (
                <>
                  <MDTypography variant="caption" fontWeight="medium">
                    Ücret {safeText(session.payrollLeaderMemberName)} üyesinde
                  </MDTypography>
                  <MDTypography
                    variant="caption"
                    sx={{ color: 'text.secondary', display: 'block' }}
                  >
                    Aynı grup seansının ücreti tek kez sayıldı
                  </MDTypography>
                </>
              )}
            </BodyCell>

            <BodyCell align="center">
              {session.isPayrollLeader ? (
                <Checkbox
                  size="small"
                  checked={Boolean(session.includedInPayroll)}
                  onChange={(e) => onToggleIncluded?.(session, e.target.checked)}
                />
              ) : (
                <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
                  Bilgi
                </MDTypography>
              )}
            </BodyCell>

            <BodyCell align="right">
              {session.isPayrollLeader ? (
                <TextField
                  size="small"
                  type="number"
                  value={session.payrollAmountTry ?? ''}
                  onChange={(e) => onChangePayrollAmount?.(session, e.target.value)}
                  sx={{
                    width: 110,
                    '& .MuiInputBase-root': {
                      height: 36,
                      borderRadius: '10px',
                      backgroundColor: '#fff',
                    },
                    '& .MuiInputBase-input': {
                      textAlign: 'right',
                      fontSize: 13,
                    },
                  }}
                />
              ) : (
                <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
                  Dahil edildi
                </MDTypography>
              )}
            </BodyCell>
          </MDBox>
        );
      })}
    </MDBox>
  );
}

function MemberAccordion({ member, expanded, onToggle, onToggleIncluded, onChangePayrollAmount }) {
  const earnedText = `${Math.round(Number(member.summary.earningTry || 0)).toLocaleString(
    'tr-TR'
  )} ₺`;
  const redirectedText =
    member.summary.redirectedSessions > 0
      ? `${member.summary.redirectedSessions} seansın ücreti başka üyede`
      : `${member.summary.includedSessions} seans bu üyede ücretlendi`;

  return (
    <Accordion
      expanded={expanded}
      onChange={onToggle}
      disableGutters
      elevation={0}
      sx={{
        borderRadius: '16px',
        border: '1px solid rgba(15,23,42,0.08)',
        overflow: 'hidden',
        '&:before': { display: 'none' },
        bgcolor: '#fff',
      }}
    >
      <AccordionSummary
        expandIcon={<Icon>{expanded ? 'expand_less' : 'expand_more'}</Icon>}
        sx={{
          px: 1.2,
          py: 0.25,
          minHeight: 68,
          '& .MuiAccordionSummary-content': { my: 0 },
        }}
      >
        <MDBox
          sx={{
            width: '100%',
            display: 'grid',
            gridTemplateColumns: 'minmax(220px, 1.5fr) 110px 130px minmax(190px, 1fr)',
            gap: 1,
            alignItems: 'center',
          }}
        >
          <MDBox>
            <MDTypography variant="button" fontWeight="bold">
              {member.memberName}
            </MDTypography>
            <MDTypography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
              {member.packageNames?.length ? member.packageNames.join(', ') : 'Paket bilgisi yok'}
            </MDTypography>
          </MDBox>

          <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
            {member.summary.total} seans
          </MDTypography>

          <MDTypography variant="caption" fontWeight="bold" sx={{ color: 'success.main' }}>
            {earnedText}
          </MDTypography>

          <MDBox>
            <MDTypography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
              {redirectedText}
            </MDTypography>
            {member.summary.warningCount > 0 ? (
              <MDTypography variant="caption" sx={{ color: 'warning.main', display: 'block' }}>
                {member.summary.warningCount} fiyat uyarısı var
              </MDTypography>
            ) : null}
          </MDBox>
        </MDBox>
      </AccordionSummary>

      <AccordionDetails sx={{ pt: 0, px: 1.2, pb: 1.2 }}>
        <SessionTable
          sessions={member.sessions}
          onToggleIncluded={onToggleIncluded}
          onChangePayrollAmount={onChangePayrollAmount}
        />
      </AccordionDetails>
    </Accordion>
  );
}

export default function PayrollSessionsAccordion({
  rows = [],
  onToggleIncluded,
  onChangePayrollAmount,
  onExportPdf,
}) {
  const members = useMemo(() => buildGroupedView(rows), [rows]);
  const [openMemberKey, setOpenMemberKey] = useState(null);

  return (
    <MDBox
      sx={{
        p: 1.1,
        borderRadius: '22px',
        border: '1px solid rgba(15,23,42,0.08)',
        bgcolor: '#fff',
      }}
    >
      <MDBox
        sx={{
          px: 0.2,
          pb: 0.8,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <MDBox>
          <MDTypography variant="button" fontWeight="bold">
            Üye Bazlı Seanslar
          </MDTypography>
          <MDTypography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
            Üyeyi açmadan paketini, seans sayısını ve kazancını gör. Açınca ödeme ve hakediş
            detayları tablo olarak gelsin.
          </MDTypography>
        </MDBox>

        <Tooltip title="PDF paylaş">
          <IconButton size="small" onClick={onExportPdf}>
            <Icon fontSize="small">share</Icon>
          </IconButton>
        </Tooltip>
      </MDBox>

      <MDBox sx={{ display: 'grid', gap: 0.75 }}>
        {members.length ? (
          members.map((member) => (
            <MemberAccordion
              key={member.memberKey}
              member={member}
              expanded={openMemberKey === member.memberKey}
              onToggle={() =>
                setOpenMemberKey((prev) => (prev === member.memberKey ? null : member.memberKey))
              }
              onToggleIncluded={onToggleIncluded}
              onChangePayrollAmount={onChangePayrollAmount}
            />
          ))
        ) : (
          <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
            Bu filtrelerle gösterilecek seans yok.
          </MDTypography>
        )}
      </MDBox>
    </MDBox>
  );
}
